import { useState } from "react";
import { Grid, GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import {
  useDealHistory,
  useGroupedHistory,
  useHistoryWithNotes,
  HistoryDetailGroup,
  HistoryDetailResolvedRow,
  HistoryTimelineItem,
} from "src/dm/hooks/useDealActivity";
import UserName from "src/dm/components/UserName/UserName";

const fmtWhen = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

/**
 * SPA port of legacy `ViewDealHistory.aspx`.
 *
 * Two views are available:
 *
 *   - **Status changes** — the existing tabular DEAL_CHANGE_HIST view
 *     with the "(View Changes)" drill-down. Drill-down now uses the
 *     grouped + lookup-resolved endpoint so each TBL_NME gets its own
 *     section and coded columns render as human descriptions
 *     ("Print" instead of "P", etc.) — see audit 11 §20.
 *
 *   - **Timeline** — interleaves DEAL_NOTE entries with the status
 *     changes in chronological order. Mirrors the legacy code that
 *     calls `DealNote.GetByDateRange` between two history timestamps
 *     so notes appear inline with status changes.
 */
const DealHistoryPage = () => {
  const deal = useDealContext();
  const { data, isLoading, isError, error } = useDealHistory(deal.dealId);
  const [drillId, setDrillId] = useState<number | null>(null);
  const [view, setView] = useState<"status" | "timeline">("status");
  const grouped = useGroupedHistory(deal.dealId, drillId);
  const timeline = useHistoryWithNotes(deal.dealId);

  if (isLoading) return <p>Loading history&hellip;</p>;
  if (isError)
    return <p style={{ color: "#b91c1c" }}>{error instanceof Error ? error.message : "Failed to load"}</p>;

  const rows = (data ?? []).map((r) => ({ ...r, changeDateText: fmtWhen(r.changeDate) }));

  const DrillCell = (props: GridCellProps) => {
    const row = props.dataItem as { historyId: number };
    return (
      <td>
        <Button
          type="button"
          size="small"
          fillMode="flat"
          onClick={() => setDrillId(row.historyId)}
        >
          View Changes
        </Button>
      </td>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
        <h2 style={{ margin: 0 }}>History</h2>
        <div role="tablist" aria-label="History view" style={{ display: "flex", gap: "0.25rem" }}>
          <Button
            type="button"
            size="small"
            fillMode={view === "status" ? "solid" : "outline"}
            themeColor={view === "status" ? "primary" : "base"}
            onClick={() => setView("status")}
          >
            Status changes
          </Button>
          <Button
            type="button"
            size="small"
            fillMode={view === "timeline" ? "solid" : "outline"}
            themeColor={view === "timeline" ? "primary" : "base"}
            onClick={() => setView("timeline")}
            title="Status changes interleaved with deal notes (legacy ViewDealHistory timeline)"
          >
            Timeline with notes
          </Button>
        </div>
      </div>

      {view === "status" && (
        <>
          {rows.length === 0 ? (
            <p style={{ color: "#94a3b8" }}>No status changes recorded.</p>
          ) : (
            <Grid data={rows}>
              <GridColumn field="historyId" title="#" width="80px" />
              <GridColumn field="status" title="Status" width="140px" />
              <GridColumn field="userId" title="By" width="220px" />
              <GridColumn field="changeDateText" title="When" />
              <GridColumn title="" width="140px" cells={{ data: DrillCell }} />
            </Grid>
          )}
        </>
      )}

      {view === "timeline" && (
        <TimelineView items={timeline.data} isLoading={timeline.isLoading} error={timeline.error} />
      )}

      {drillId !== null && (
        <Dialog
          title={`Changes for history entry #${drillId}`}
          onClose={() => setDrillId(null)}
          width={920}
        >
          {grouped.isLoading && <p>Loading details&hellip;</p>}
          {grouped.isError && (
            <p style={{ color: "#b91c1c" }}>
              {grouped.error instanceof Error ? grouped.error.message : "Failed to load details"}
            </p>
          )}
          {grouped.data && grouped.data.length === 0 && (
            <p style={{ color: "#94a3b8" }}>No field-level detail rows for this entry.</p>
          )}
          {grouped.data && grouped.data.length > 0 && (
            <GroupedDetails groups={grouped.data} />
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.75rem" }}>
            <Button onClick={() => setDrillId(null)}>Close</Button>
          </div>
        </Dialog>
      )}
    </div>
  );
};

interface GroupedDetailsProps {
  groups: HistoryDetailGroup[];
}

const GroupedDetails = ({ groups }: GroupedDetailsProps) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
    {groups.map((g) => (
      <section key={g.tableName} style={{ border: "1px solid #e2e8f0", borderRadius: 6 }}>
        <header
          style={{
            background: "#f1f5f9",
            padding: "0.4rem 0.75rem",
            borderBottom: "1px solid #e2e8f0",
            borderTopLeftRadius: 6,
            borderTopRightRadius: 6,
            fontSize: "0.85rem",
            fontWeight: 600,
            color: "#334155",
          }}
        >
          {g.tableName}
          <span style={{ color: "#64748b", fontWeight: 400, marginLeft: "0.5rem" }}>
            ({g.rows.length} row{g.rows.length === 1 ? "" : "s"})
          </span>
        </header>
        <Grid data={g.rows}>
          <GridColumn field="columnName" title="Column" width="220px" />
          <GridColumn field="operationType" title="Op" width="80px" />
          <GridColumn
            field="oldValue"
            title="Old Value"
            cells={{ data: (p: GridCellProps) => valueCell(p, "old") }}
          />
          <GridColumn
            field="newValue"
            title="New Value"
            cells={{ data: (p: GridCellProps) => valueCell(p, "new") }}
          />
        </Grid>
      </section>
    ))}
  </div>
);

// Render the raw code plus the lookup-resolved description (when present).
const valueCell = (props: GridCellProps, which: "old" | "new") => {
  const row = props.dataItem as HistoryDetailResolvedRow;
  const raw = which === "old" ? row.oldValue : row.newValue;
  const resolved = which === "old" ? row.oldValueResolved : row.newValueResolved;
  return (
    <td>
      {raw || <span style={{ color: "#94a3b8" }}>&mdash;</span>}
      {resolved && (
        <span style={{ color: "#64748b", marginLeft: "0.4rem", fontSize: "0.8rem" }}>
          ({resolved})
        </span>
      )}
    </td>
  );
};

interface TimelineViewProps {
  items: HistoryTimelineItem[] | undefined;
  isLoading: boolean;
  error: unknown;
}

const TimelineView = ({ items, isLoading, error }: TimelineViewProps) => {
  if (isLoading) return <p>Loading timeline&hellip;</p>;
  if (error) {
    return <p style={{ color: "#b91c1c" }}>{error instanceof Error ? error.message : "Failed to load"}</p>;
  }
  if (!items || items.length === 0) {
    return <p style={{ color: "#94a3b8" }}>No timeline entries.</p>;
  }
  return (
    <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "0.6rem" }}>
      {items.map((it, idx) => {
        const isNote = it.kind === "note";
        return (
          <li
            key={`${it.kind}-${it.historyId ?? it.noteId ?? idx}`}
            style={{
              border: `1px solid ${isNote ? "#bae6fd" : "#e2e8f0"}`,
              background: isNote ? "#f0f9ff" : "#ffffff",
              padding: "0.5rem 0.75rem",
              borderRadius: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", color: "#475569" }}>
              <span
                style={{
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  padding: "0.1rem 0.4rem",
                  borderRadius: "999px",
                  background: isNote ? "#0ea5e9" : "#475569",
                  color: "#fff",
                }}
              >
                {it.kind}
              </span>
              <span style={{ fontWeight: 600, color: "#0f172a" }}>{it.label ?? "—"}</span>
              <span style={{ marginLeft: "auto" }}>{fmtWhen(it.when)}</span>
              {/* Resolve actor DOMAIN\username → display name per
                  audit/21-visual-diff §12. */}
              <span>· <UserName userId={it.actor} /></span>
            </div>
            {it.text && (
              <p style={{ margin: "0.4rem 0 0", fontSize: "0.9rem", color: "#1e293b", whiteSpace: "pre-wrap" }}>
                {it.text}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default DealHistoryPage;
